import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Home.css';

const Home = () => {
  const { user, loading } = useAuth();
  const [expandedFacts, setExpandedFacts] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  const toggleFact = (index) => {
    setExpandedFacts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const handleOverlayPlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play().catch((err) => console.warn('Video play failed:', err));
      setIsVideoPlaying(true);
    }
  };

  const facts = [
    {
      title: "Prestasjonsfremmende effekter..",
      content: [
        "Synkron musikk (tilpasset løpefrekvens) kan øke utholdenhet med 15-20% (Karageorghis et al., 2009)",
        "Musikk med tempo mellom 160-180 BPM matcher optimal løpefrekvens for mosjonister (Bood et al., 2013)",
        "Løpere som synkroniserer skrittene sine med musikkrytmen forbedrer løpeøkonomien med 5-7% (Bacon et al., 2012)"
      ]
    },
    {
      title: "Skreddersydd musikk..",
      content: [
        "med nøyaktig 165-180 BMP som hjelper deg å opprettholde optimal løpefrekvens for letthet og skadeforebygging."
      ]
    },
    {
      title: "Tidsbasert løping..",
      content: [
        "Forskning viser at løping basert på tid fremfor distanse gir bedre fremgang og mer glede. Vi kan guide deg gjennom hele økten."
      ]
    },
    {
      title: "Nesepust og anspenthet..",
      content: [
        "Våre instruksjoner hjelper deg å mestre pusteteknikker som øker kapasiteten og gir en mer avslappet løpeopplevelse."
      ]
    },
    {
      title: "Høy frekvens for store fordeler..",
      content: [
        "Studier viser at løping med høy frekvens /165-189 steg per minutt) reduserer belastning på ledd og muskler med opptil 20% og forebygger skader effektivt."
      ]
    },
    {
      title: "Musikk påvirker hjernen",
      content: [
        "Rytmisk musikk stimulerer hjernens belønningssystem og frigjør endorfiner som reduserer opplevd anstrengelse med opptil 15% under fysisk aktivitet."
      ]
    },
    {
      title: "Frekvens og skadeforebygging..",
      content: [
        "Økning av løpefrekvens til 170-180 skritt per minutt reduserer belastningskrefter med opptil 20% (Heiderscheit et al., 2011)",
        "Høyere løpefrekvens (guidet av musikktempo) reduserer skrttlengde og vertikal oscillasjon, noe som minsker belastning på ledd (Willy et al., 2016)",
        "Musikk-guidet frekvens-trening resulterte i 27% færre løpsrelaterte skader sammenlignet med kontrollgrupper i en 6-måneders intervensjonsstudie (Van Gent et al., 2019)"
      ]
    }
  ];

  return (
    <div className="home-page">
      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-backdrop"
          onClick={toggleMobileMenu}
        ></div>
      )}

      {/* Top Bar / Navbar */}
      <nav className="top-bar">
        <div className="navbar-content">
          <div className="navbar-logo">
            <img src="/img/lopeglad.png" alt="lopeglad logo" className="logo-image" />
          </div>
          <button 
            className="hamburger-menu"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>
          <div className={`navbar-menu ${mobileMenuOpen ? 'open' : ''}`}>
            {!loading && user ? (
              <Link to="/dashboard" className="login-button" onClick={() => setMobileMenuOpen(false)}>DASHBOARD</Link>
            ) : (
              <Link to="/login" className="login-button" onClick={() => setMobileMenuOpen(false)}>LOGG INN</Link>
            )}
          </div>
        </div>
      </nav>

      {/* Header/Hero Section */}
      <header className="hero-section">
        <div className="hero-background">
          <img src="/img/background image.jpg" alt="Running background" />
        </div>
        <div className="hero-content">
          <h2 className="hero-headline">Løp lettere, smartere og med mer glede</h2>
          <p className="hero-free-trial"><strong>NYHET: 7 dager gratis prøveperiode</strong></p>
          <div className="hero-cta">
            <button 
              className="cta-secondary"
              onClick={() => {
                const subscriptionSection = document.querySelector('.subscription-section');
                subscriptionSection?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              SE PRISER
            </button>
            <Link to="/create-account" className="cta-primary">
              REGISTRER DEG FOR Å FÅ TILGANG I APPEN
              <span className="arrow">→</span>
            </Link>
          </div>
        </div>
        <div className="mobile-phones">
          <img src="/img/apps-scaled.png" alt="App screenshots" className="apps-image" />
        </div>
      </header>

      {/* Feature Section 1 */}
      <section className="feature-section-1">
        <img src="/img/blob-light-green.svg" alt="" className="blob-decoration blob-top" />
        <svg className="wave-svg wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path fill="#A1D07F" d="M0,0 L1440,0 L1440,80 Q720,120 0,80 Z"/>
        </svg>
        <div className="feature-content">
          <div className="feature-phone">
            <img src="/img/phone.png" alt="Phone mockup" />
          </div>
          <div className="feature-text">
            <h3>Økt 2 mot bedre form</h3>
            <p>
              Vi kombinerer skreddersydd tilpasset musikk med profesjonell menneskelig lydinstruksjon for å gjøre hver løpetur til en opplevelse av mestring – ikke bare prestasjon
            </p>
          </div>
        </div>
      </section>

      {/* Subscription Section */}
      <section id="abonnement" className="subscription-section">
        <img src="/img/blob-medium-green.svg" alt="" className="blob-decoration blob-top" />
        <h2 className="section-title">Abonnement</h2>
        <div className="pricing-cards">
            <div className="pricing-badge">Best valg for nybegynnere</div>
          <div className="pricing-card">
            <h3>Full tilgang</h3>
            <div className="price">
              <span className="price-amount">149</span>
              <span className="price-unit">NOK /mnd</span>
            </div>
            <ul className="features-list">
              <li>Ubegrenset tilgang til økter</li>
              <li className="feature-strong"><strong>Alle treningsprogram</strong></li>
              <li>Lag egne spillelister</li>
              <li>Dine favoritter</li>
              <li>Finn ditt nivå</li>
            </ul>
          </div>
          <div className="pricing-card pricing-card-secondary">
            <h3>Av og til</h3>
            <div className="price">
              <span className="price-amount">99</span>
              <span className="price-unit">NOK /mnd</span>
            </div>
            <ul className="features-list">
              <li>Ubegrenset tilgang til økter</li>
              <li>Lag egne spillelister</li>
              <li>Dine favoritter</li>
              <li>Finn ditt nivå</li>
            </ul>
          </div>
        </div>
        <p className="hero-free-trial-white"><strong>NYHET: 7 dager gratis prøveperiode</strong></p>

        <Link to="/subscriptions/plans" className="cta-primary subscription-cta">
          KJØP ABONNEMENT HER
          <span className="arrow">→</span>
        </Link>

        <p className="subscription-info">Ingen bindingstid, abonnementet går til du selv stopper det.</p>
        <div className="app-store-badges">
          <a href="https://play.google.com/store/apps/details?id=com.app.lopeglad&pcampaignid=web_share" className="app-badge" target="_blank" rel="noopener noreferrer" aria-label="Last ned på Google Play">
            <img src="/img/GooglePlayEnglish.png" alt="Get it on Google Play" className="app-badge-img" />
          </a>
          <a href="https://apps.apple.com/no/app/l%C3%B8peglad/id6741588236?l=nb" className="app-badge" target="_blank" rel="noopener noreferrer" aria-label="Last ned på App Store">
            <img src="/img/AppStore.png" alt="Download on the App Store" className="app-badge-img" />
          </a>
        </div>

      </section>

      {/* Video Section */}
      <section className="video-section">
        <div className="video-container">
          <video 
            ref={videoRef}
            controls 
            className="main-video"
            preload="metadata"
            playsInline
            onPlay={handleVideoPlay}
            onPause={handleVideoPause}
          >
            <source 
              src="https://lopeglad.no/wp-content/uploads/2025/05/att.W_r2w6pIpE5EhnfoEpmSiSYRQUrEPUyqIuFbDzHQ3jA.mp4" 
              type="video/mp4" 
            />
            Your browser does not support the video tag.
          </video>
          <div 
            className={`video-overlay ${isVideoPlaying ? 'hidden' : ''}`}
            onClick={handleOverlayPlayClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOverlayPlayClick(); }}
            role="button"
            tabIndex={0}
            aria-label="Spill av video"
          >
            <img src="/img/background image.jpg" alt="" className="overlay-image" />
            <div className="video-play-icon">
              <i className="fas fa-play"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section">
        <img src="/img/blob.svg" alt="" className="blob-decoration blob-top" />
        <svg className="wave-svg wave-bottom" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path fill="#023D31" d="M0,40 Q720,0 1440,40 L1440,120 L0,120 Z"/>
        </svg>
        <h2 className="testimonial-headline">Jeg har fått en bedre teknikk..</h2>
        <h3>..ved hjelp av appen, og på den måten kan jeg få til å løpe lengre. Liker godt å ha stemmen og påminnelser på øret om hvor fokus skal være»</h3>
        <div className="testimonials-grid">
          <div className="testimonial-column">
            <p>- Ellen, 46 år</p>
            <p>Løpegleden har gitt meg tilbake troen på at jeg kan være en løper og bidratt til å gi meg glede ved løping.</p>
          </div>
          <div className="testimonial-column">
            <p>- Anne Marit, 59 år</p>
            <p>Gir meg mestringsfølelse og følelsen av å ha min egen personlige trener som støtter og pusher meg hele veien.</p>
          </div>
          <div className="testimonial-column">
            <p>- Stian, 36 år</p>
            <p>Fader den økta funka! Jeg var helt oppe i 12km/t på flere drag og det kjentes helt greit ut. Det har aldri skjedd før. Hadde god kontroll på pulsen også. Dette var gøy!</p>
          </div>
        </div>
        <img src="/img/blob2.svg" alt="" className="blob-decoration blob-bottom" />
      </section>

      {/* Podcast Section */}
      <section className="podcast-section">
        <div className="podcast-logo-container">
          <img src="/img/Podkast-02.png" alt="Lopeglad Podkast" className="podcast-logo-image" />
        </div>
        <div className="podcast-iframe-container">
          <iframe 
            src="https://embed.acast.com/68a739eae2f63983a7306bea?feed=true" 
            frameBorder="0" 
            width="100%" 
            height="280px"
            title="Lopeglad Podkast"
            allow="autoplay"
          ></iframe>
        </div>
      </section>

      {/* Feature Section 2 */}
      <section className="feature-section-2">
        <img src="/img/blob.svg" alt="" className="blob-decoration blob-top" />
        <svg className="wave-svg wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path fill="#A1D07F" d="M0,0 L1440,0 L1440,80 Q720,120 0,80 Z"/>
        </svg>
        <div className="feature-content-2">
          <div className="feature-phone-2">
            <img src="/img/phone2.png" alt="Phone mockup" />
          </div>
          <div className="feature-list">
            <div className="feature-item">
              <h3>Fast BPM</h3>
              <p>For å holde optimal rytme og tempo, gir vi tilpasset løping med 160-185 steg i minuttet.</p>
            </div>
            <div className="feature-item">
              <h3>Motiverende stemme</h3>
              <p>Elin, utdannet fysioterapeut, er profesjonell trener som gir tips og oppmuntring underveis i økten.</p>
            </div>
            <div className="feature-item">
              <h3>Løp mot et mål</h3>
              <p>Løpeappen har mange fine programmer som du kan bruke for å forbedre din løpeteknikk og nå dine mål.</p>
            </div>
          </div>
        </div>
        <img src="/img/blob2.svg" alt="" className="blob-decoration blob-bottom" />
      </section>

      {/* Team Section */}
      <section id="team" className="team-section">
        <h2 className="section-title">Møt teamet</h2>
        <div className="team-container">
          <div className="team-member-card">
            <div className="team-image-wrapper">
              <img src="/img/teamet_Elin.png" alt="Elin" />
            </div>
            <h4>Elin</h4>
          </div>
          <div className="team-member-card">
            <div className="team-image-wrapper">
              <img src="/img/teamet_Larserik.png" alt="Larserik" />
            </div>
            <h4>Lars-Erik</h4>
          </div>
        </div>
      </section>

      {/* Visste du at Section */}
      <section className="facts-section">
        <img src="/img/blob.svg" alt="" className="blob-decoration blob-top" />
        <svg className="wave-svg wave-top" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path fill="#A1D07F" d="M0,0 L1440,0 L1440,80 Q720,120 0,80 Z"/>
        </svg>
        <h2 className="section-title">Visste du at?</h2>
        <div className="facts-list">
          {facts.map((fact, index) => (
            <div 
              key={index} 
              className={`fact-item ${expandedFacts[index] ? 'expanded' : ''}`}
              onClick={() => toggleFact(index)}
            >
              <div className="fact-header">
                <span className="fact-title">{fact.title}</span>
                <span className="fact-toggle">{expandedFacts[index] ? '−' : '+'}</span>
              </div>
              {expandedFacts[index] && (
                <div className="fact-content">
                  {fact.content.map((item, i) => (
                    <p key={i}>{item}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
      <img src="/img/blob2-light-green.svg" alt="" className="blob-decoration blob-footer-top" />
        <div className="footer-content">
          <div className="footer-logo">
            <img src="/img/lopeglad.png" alt="lopeglad logo" className="footer-logo-image" />
          </div>
          <div className="footer-nav">
            <div className="nav-column">
              <h4>Finn ut mer</h4>
              <ul>
                <li><a href="#abonnement">Abonnement</a></li>
                <li><a href="#team">Teamet</a></li>
                <li><a href="mailto:post@lopeglad.no">post@lopeglad.no</a></li>
              </ul>
            </div>
            <div className="nav-column">
              <h4>Bedriften</h4>
              <ul>
                <li><Link to="/vilkår">Vilkår</Link></li>
                <li><Link to="/personvern">Personvern</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-social">
            <a href="https://www.facebook.com/lopeglad.no" className="social-icon"><i className="fab fa-facebook"></i></a>
            <a href="
            https://www.instagram.com/lopeglad.no/" className="social-icon"><i className="fab fa-instagram"></i></a>
            <a href="https://www.youtube.com/@lopeglad" className="social-icon"><i className="fab fa-linkedin"></i></a>
          </div>
          <div className="footer-apps">
            <a href="https://play.google.com/store/apps/details?id=com.app.lopeglad&pcampaignid=web_share" className="app-badge" target="_blank" rel="noopener noreferrer" aria-label="Last ned på Google Play">
              <img src="/img/GooglePlayEnglish.png" alt="Get it on Google Play" className="app-badge-img" />
            </a>
            <a href="https://apps.apple.com/no/app/l%C3%B8peglad/id6741588236?l=nb" className="app-badge" target="_blank" rel="noopener noreferrer" aria-label="Last ned på App Store">
              <img src="/img/AppStore.png" alt="Download on the App Store" className="app-badge-img" />
            </a>
          </div>
        </div>
        <div className="footer-copyright">
          <p>© 2023 Lopeglad</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;

